// noinspection JSFileReferences

import Map from "ol/Map.js";
import View from "ol/View.js";
import {Circle as CircleStyle, Fill, Stroke, Style} from "ol/style.js";
import {defaults as defaultControls} from "ol/control.js";
import {Draw, Modify, Select, Snap, DragRotateAndZoom, defaults as defaultInteractions} from 'ol/interaction.js';
import {OSM, Vector as VectorSource} from "ol/source.js";
import {Tile as TileLayer, Vector as VectorLayer} from "ol/layer.js";
import {useGeographic, get} from "ol/proj.js";
import {Vector} from "ol/layer";
import {Icon} from "ol/style";
import {Feature} from "ol";
import {Geometry, Point, Polygon} from "ol/geom";
import {GeoJSON} from "ol/format";

export class MapView {
	mapContainer;
	lonLat;
	markerLonLat;
	layerMapRaster;
	map;
	pinFeature;
	pinSource;
	pinLayer;

	constructor(outputTo) {
		useGeographic();
		this.mapContainer = outputTo;
		this.lonLat = [
			outputTo.dataset.lon || -1.4649002412640562,
			outputTo.dataset.lat || 52.92761917315043,
		];
		this.zoom = 13;

		if(outputTo.dataset.lon && outputTo.dataset.lat) {
			this.markerLonLat = [
				outputTo.dataset.lon,
				outputTo.dataset.lat,
			]
		}
		else {
			this.zoom = 5;
		}

		setTimeout(() => {
				this.mapContainer.querySelector(".ol-attribution button").click();
			},
			250
		);

		this.layerMapRaster = new TileLayer({
			source: new OSM(),
		});
		this.map = new Map({
			interactions: defaultInteractions().extend([new DragRotateAndZoom()]),
			target: this.mapContainer,
			layers: [this.layerMapRaster],
			view: new View({
				center: this.lonLat,
				zoom: this.zoom,
			}),
			controls: defaultControls({ rotate: false }),
		});

		// Initialize pin if dataset has pin property
		if(this.mapContainer.dataset.pin !== undefined) {
			this.initializePin();
		}

		// Add click event listener to the map
		this.map.on('click', (event) => {
			const coordinates = event.coordinate;
			this.createOrMovePin(coordinates);
		});
	}

	initializePin() {
		// Only create the pin if the initial location is available
		if(this.markerLonLat) {
			this.createOrMovePin(this.markerLonLat);
		}
	}

	createOrMovePin(coordinates) {
		// If pin already exists, update its position
		if(this.pinFeature) {
			this.pinFeature.getGeometry().setCoordinates(coordinates);
		}
		// Otherwise create a new pin
		else {
			// Create pin feature
			this.pinFeature = new Feature({
				geometry: new Point(coordinates),
			});

			const pinStyle = new Style({
				image: new Icon({
					src: "/asset/map-icon/empty-pin.svg",
					anchor: [0.5, 1], // Adjust anchor point as needed
				}),
			});

			this.pinFeature.setStyle(pinStyle);

			// Create source and layer for the pin
			this.pinSource = new VectorSource({
				features: [this.pinFeature],
			});

			this.pinLayer = new VectorLayer({
				source: this.pinSource,
			});

			// Add the pin layer to the map
			this.map.addLayer(this.pinLayer);

			// Add modify interaction to allow dragging the pin
			const modify = new Modify({
				source: this.pinSource,
				// Set condition to only activate on drag, not on click
				condition: (event) => {
					// Only activate modify on pointerdown events when directly over the feature
					if (event.type === 'pointerdown') {
						const pixel = this.map.getEventPixel(event.originalEvent);
						let hit = false;
						this.map.forEachFeatureAtPixel(pixel, (feature) => {
							if (feature === this.pinFeature) {
								hit = true;
								return true;
							}
						});
						return hit;
					}
					// For all other event types, only activate if it's not a click
					return event.type !== 'click';
				}
			});
			modify.on('modifyend', (event) => {
				const feature = event.features.getArray()[0];
				const geometry = feature.getGeometry();
				const coordinates = geometry.getCoordinates();
				this.updateFormCoordinates(coordinates);
			});
			this.map.addInteraction(modify);
		}

		// Update form with new coordinates
		this.updateFormCoordinates(coordinates);
	}

	updateFormCoordinates(coordinates) {
		let [lon, lat] = coordinates;
		lon = typeof lon === 'string' ? parseFloat(lon) : lon;
		lat = typeof lat === 'string' ? parseFloat(lat) : lat;

		let form = this.mapContainer.closest("form");
		if(form) {
			form["lat"].value = lat.toFixed(5);
			form["lon"].value = lon.toFixed(5);
		}
	}
}
